import { useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { NAV_LINKS, BRAND } from "../data/content";
import GoldButton from "./GoldButton";

export default function MobileNav({ open, onClose }) {
    // Lock body scroll while the overlay is open.
    useEffect(() => {
        if (open) {
            const previous = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = previous;
            };
        }
    }, [open]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    suppressHydrationWarning
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="fixed inset-0 z-[60] bg-[#0B0B0C] text-[#F3EFE7]"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Mobile navigation"
                    data-testid="mobile-nav-overlay"
                >
                    <div className="shell flex items-center justify-between h-[76px] md:h-[84px] border-b border-[rgba(201,168,106,0.15)]">
                        <span className="text-[11px] tracking-[0.32em] uppercase text-[#C9A86A]">
                            ACT · Menu
                        </span>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close menu"
                            className="inline-flex items-center justify-center w-11 h-11 text-[#F3EFE7] hover:text-[#C9A86A] transition-colors"
                            data-testid="mobile-menu-close"
                        >
                            <X size={22} strokeWidth={1.2} />
                        </button>
                    </div>

                    <div className="shell py-14 flex flex-col justify-between min-h-[calc(100vh-84px)]">
                        <nav aria-label="Mobile primary" className="flex flex-col gap-8">
                            {NAV_LINKS.map((link, idx) => (
                                <motion.div
                                    key={link.to}
                                    suppressHydrationWarning
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.6,
                                        delay: 0.08 + idx * 0.05,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                >
                                    <Link
                                        to={link.to}
                                        onClick={onClose}
                                        className="font-serif text-5xl sm:text-6xl text-[#F3EFE7] hover:text-[#C9A86A] transition-colors"
                                        data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                                    >
                                        {link.label}
                                    </Link>
                                </motion.div>
                            ))}
                        </nav>

                        <div className="mt-16 space-y-8">
                            <GoldButton
                                to="/contact"
                                onClick={onClose}
                                className="w-full"
                                testId="mobile-nav-book-consultation"
                            >
                                Book Consultation
                            </GoldButton>

                            <div className="hairline-solid" />

                            <div className="space-y-2 text-[13px] text-[#9A958A]">
                                <a
                                    href={BRAND.phoneHref}
                                    className="block text-[#F3EFE7] hover:text-[#C9A86A] transition-colors"
                                >
                                    {BRAND.phone}
                                </a>
                                <a
                                    href={BRAND.emailHref}
                                    className="block text-[#F3EFE7] hover:text-[#C9A86A] transition-colors break-all"
                                >
                                    {BRAND.email}
                                </a>
                                <p className="pt-2">{BRAND.region}</p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
